// Dark-mode palette — values sourced from Figma TDS Foundations (Mode.zip, Dark.tokens.json).
import type {
  Accent,
  Additional,
  Background,
  Category,
  Core,
  DeepGrey,
  DeepPurple,
  Disabled,
  Error,
  Ghost,
  Green,
  Icons,
  Orange,
  Overlay,
  Primary,
  Purple,
  Red,
  Secondary,
  Success,
  Tertiary,
  Text,
  Theme,
  Transparent,
  Turquoise,
  Warning,
} from './themes'

export const additional = {
  accentElement: '#c2c2c2',
} as const satisfies Additional

export const background = {
  main: '#131315',
  accent: '#1d1d20',
  colorAccent: '#9a80c1', // '#420A87, transparent 4%',
} as const satisfies Background

export const disabled = {
  main: '#5c5c5c',
  accent: '#5c5c5c',
  lowContrast: '#928f8f',
  mediumContrast: '#5c5c5c',
} as const satisfies Disabled

export const error = {
  main: '#ed5a5a',
  mediumContrast: '#ED5A5A52', // #ED5A5A, transparent 32%
  lowContrast: '#ED5A5A29', // #ED5A5A, transparent 16%
} as const satisfies Error

export const ghost = {
  main: '#7b7b7b',
  mediumContrast: '#878383',
  lowContrast: '#928f8f',
} as const satisfies Ghost

export const icons = {
  main: '#7b7b7b',
  logo: '#fffffa',
} as const satisfies Icons

export const overlay = {
  main: '#928f8f',
  accent: '#5c5c5c',
  tooltip: '#0a0c0c',
} as const satisfies Overlay

export const primary = {
  maxContrast: '#7566a7',
  highContrast: '#9a80c1',
  upperMidContrast: '#ffffff',
  midContrast: '#ffffff',
  lowerMidContrast: '#4b3d7a',
  lowContrast: '#393056',
  reversedContrast: '#f5f6f7',
  // legacy aliases
  mainContrast: '#9a80c1',
  mediumContrast: '#4b3d7a',
} as const satisfies Primary

export const tertiary = {
  maxContrast: '#e6e6e6',
  highContrast: '#b3b3b3',
  upperMidContrast: '#7b7b7b',
  midContrast: '#5c5c5c',
  lowerMidContrast: '#5c5c5c',
  lowContrast: '#928f8f',
  reversedContrast: '#f5f6f7',
  // legacy aliases
  main: '#b3b3b3',
  upperMediumContrast: '#7b7b7b',
  mediumContrastAlternative: '#5c5c5c',
  mediumContrast: '#5c5c5c',
} as const satisfies Tertiary

export const secondary = {
  maxContrast: '#00b7b3',
  highContrast: '#019693',
  upperMidContrast: '#01706e',
  midContrast: '#002e2d',
  lowerMidContrast: '#ffffff',
  lowContrast: '#ffffff',
  reversedContrast: '#f5f6f7',
  // legacy aliases
  mainContrast: '#019693',
  mediumContrast: '#ffffff',
} as const satisfies Secondary

export const accent = {
  maxContrast: '#7566a7',
  highContrast: '#9a80c1',
  lowerMidContrast: '#4b3d7a',
  lowContrast: '#393056',
  surfaceElement: '#c2c2c2',
} as const satisfies Accent

export const success = {
  main: '#628241',
  mediumContrast: '#5b6a51',
  lowContrast: '#1d2b20',
} as const satisfies Success

export const warning = {
  main: '#e18822',
  mediumContrast: '#7c5933',
  lowContrast: '#80623f',
} as const satisfies Warning

export const text = {
  title: '#e6e6e6',
  body: '#b3b3b3',
  description: '#7b7b7b',
  caption: '#878383',
  titleReversed: '#fffffa',
  disabled: '#5c5c5c',
  emptyState: '#5c5c5c',
} as const satisfies Text

export const transparent = {
  background: '#FFFFFA40', // #fffffa, transparent 25%
  main: '#fffffa',
} as const satisfies Transparent

export const deepPurple = {
  100: '#918E8E1E', // 928F8F, transparent 12%
  200: '#5C5C5C3D', // 5C5C5C, transparent 24%
  300: '#878383',
  600: '#5C5C5C',
  700: '#7B7B7B',
  800: '#B3B3B3',
  900: '#E6E6E6',
  transparent900: '#E6E6E6B8', // E6E6E6, transparent 72%
} as const satisfies DeepPurple

export const deepGrey = {
  100: '#928f8f1f',
  200: '#5c5c5c3d',
  300: '#878383',
  600: '#5c5c5c',
  700: '#7b7b7b',
  800: '#b3b3b3',
  900: '#e6e6e6',
  transparent900: '#e6e6e6b8', // 1A0826, 72% transparent
} as const satisfies DeepGrey

export const core = {
  black: '#131315',
  white: '#FFFFFA',
  grey: '#1D1D20',
  lightGrey: '#F5F6F7',
  transparentWhite: '#FFFFFF40', // FFFFFA, transparent 25%
  transparentBlack: '#121214CC', // 131315, transparent black 80%
} as const satisfies Core

export const red = {
  100: '#ED5A5A29', // ED5A5A, transparent 16%
  200: '#ED5A5A52', // ED5A5A, transparent 32%
  500: '#ED5A5A',
} as const satisfies Red

export const orange = {
  100: '#80623F',
  200: '#7C5933',
  500: '#E18822',
} as const satisfies Orange

export const green = {
  100: '#1D2B20',
  200: '#5B6A51',
  500: '#628241',
} as const satisfies Green

export const turquoise = {
  100: '#00B7B3',
  200: '#019693',
  400: '#01706E',
  500: '#002E2D',
} as const satisfies Turquoise

export const purple = {
  50: '#2E2644', // 420A87, 4% transparent
  100: '#393056',
  200: '#4B3D7A',
  400: '#7566A7',
  500: '#9A80C1',
} as const satisfies Purple

export const category = {
  cash:           { main: '#00BF60', background: '#171e2d' },
  deals:          { main: '#33BBFF', background: '#1d1d40' },
  electronics:    { main: '#00BFAF', background: '#171e36' },
  entertainment:  { main: '#BFBF00', background: '#2e1e21' },
  fashion:        { main: '#FF3333', background: '#350d28' },
  financial:      { main: '#FF3366', background: '#350d2e' },
  foodDrinks:     { main: '#80BF00', background: '#261e21' },
  groceries:      { main: '#00BF60', background: '#171e2d' },
  health:         { main: '#00BF10', background: '#171e23' },
  houseGarden:    { main: '#8000BF', background: '#260738' },
  householdBill:  { main: '#FF3333', background: '#350d28' },
  kids:           { main: '#7733FF', background: '#260738' },
  onlineShopping: { main: '#4433FF', background: '#1f0d40' },
  others:         { main: '#828B99', background: '#261834' },
  pets:           { main: '#D99F2B', background: '#311a27' },
  sport:          { main: '#FF6633', background: '#351328' },
  subscription:   { main: '#FF3366', background: '#350d2e' },
  transfers:      { main: '#3388FF', background: '#1d1740' },
  travel:         { main: '#3355FF', background: '#1d1140' },
  transport:      { main: '#FF9933', background: '#351928' },
} as const satisfies Category

export const palette = {
  additional,
  background,
  disabled,
  error,
  ghost,
  icons,
  overlay,
  primary,
  tertiary,
  secondary,
  accent,
  success,
  text,
  transparent,
  warning,
  deepPurple,
  deepGrey,
  turquoise,
  green,
  orange,
  red,
  core,
  purple,
  category,
} as const satisfies Theme
